import { Show, createEffect, createSignal, on, onCleanup, onMount } from 'solid-js';
import { PlayerProviderContext } from './PlayerProviderContext';
import { useDispatch, useSelector, profileSelector } from '../store';
import { 
    MatchStatus,
    isCatchUp,
    getSessionUrl,
    getDRMSystem,
    getShakaServerKey,
    getVersion,
    isWeb,
    AgamaAnalytics,
    SessionMetadata
} from '../utils';
import { 
    fetchMatchEvents,
    fetchMatchStats,
    getPlaybackUrl,
    getMulticamUrl,
    stopPlayer, 
    setSelectedAudioTrackForCurrentPlayback,
    setSelectedTextTrackForCurrentPlayback,
    analyticsLogScreen,
    setLastPlayerError,
    setPlayerPaused,
    setAvailableAudioTracks,
    setAvailableTextTracks,
    resetLastPlayerError,
} from '@te_react/volt-store/lib/actions'
import { Spinner, ToastType, useConfig, useConviva, useToast } from '../components';
import { PlayerOverlays, RemoteKeyPressed } from '../constants';
import { useI18n } from '../i18n';
import { ConvivaConfig, IRequestPlaybackParama, PlayerProviderProps } from './PlayerProvider.interface';
import { playerCodeToVoltCode, ssmError, ssmErrorCodes } from './errors/errorMapping';
import { ConvivaMetadata } from '@convivainc/conviva-js-coresdk';

export const PlayerProvider = (props: PlayerProviderProps) => {
    let matchEventsTimer: any = undefined
    let matchStatsTimer: any = undefined

    const [player, setPlayer] = createSignal<any>()
    const [program, setProgram] = createSignal<any>()
    const [channel, setChannel] = createSignal<any>()
    const [loading, setLoading] = createSignal<boolean>(true)
    const [gameId, setGameId] = createSignal<string>('')
    const [matchStatus, setMatchStatus] = createSignal<MatchStatus | undefined>(undefined)
    const [userPcRestrictions, setUserPcRestrictions] = createSignal<any>()
    const [isTrailer, setIsTrailer] = createSignal<boolean>(false)
    const [loadedMetaData, setLoadedMetaData] = createSignal<boolean>(false)
    const [isLiveProgram, setIsLiveProgram] = createSignal<boolean>(false)
    const [convivaConfig, setConvivaConfig] = createSignal<ConvivaConfig>()
    const [overlay, setOverlay] = createSignal<PlayerOverlays | undefined>()
    const [showParental, setShowParental] = createSignal<boolean>(false) // We can control the parental pop up with overlay from here so that we know some parental is opened for return key.
    const [keyPressed, setKeyPressed] = createSignal<RemoteKeyPressed | undefined>()
    const [isStartOverAllowed, setIsStartOverAllowed] = createSignal<boolean>(false)
    const [pausedByUser, setPausedByUser] = createSignal<boolean>(false)
    const [drmConfig, setDrmConfig] = createSignal<any>()
    const [criticalError, setCriticalError] = createSignal<{ header: String, message: string } | undefined>()
    const [streamId, setStreamId] = createSignal("")
    const [gfk, setGfk] = createSignal<any>(undefined)
    const [isFullscreen, setIsFullscreen] = createSignal<boolean>(false)
    const [adData,setAdData] = createSignal<any>()
    const [isTileDetail, setIsTileDetail] = createSignal<boolean>(false)
    const [showUnsuscribedPopUp, setShowUnsuscribedPopUp] = createSignal<boolean>(false)
    const [purchasePopUpOpen, setPurchasePopUpOpen] = createSignal<boolean>(false)

    const programId = useSelector((state: any) => state?.player?.programId)
    const channelId = useSelector((state: any) => state?.player?.channelId)
    const multicamData = useSelector((state: any) => state?.player?.multicamData)
    const isStartOver = useSelector((state: any) => state?.player?.isStartOver)
    const matches = useSelector((state: any) => state?.opta?.matches)
    const playerError: any = useSelector((state: any) => state.player?.lastPlayerError)
    const profile = useSelector(profileSelector)
    const programsState = useSelector((state: any) => state.programs)

    const dispatch = useDispatch()
    const { getConfig } = useConfig()
    const { reportPlaybackRequested, reportPlaybackError, reportPlaybackFailed } = useConviva()
    const { getI18n } = useI18n()
    const { show } = useToast()

    const mediaEngine = getConfig()?.voltPlayer?.mediaEngine;
    const getDrmConfig = (drm: { licenseUrl: string, SSMLicenseUrl: string }) => {
        if (mediaEngine === 'nagra') {
            return {
                system: getDRMSystem(props.voltApi),
                tenantId: getConfig().nagra.playerTenantId,
                widevineLicenseUrl: drm.licenseUrl,
                SSMLicenseUrl: drm.SSMLicenseUrl,
                drmProvider: channel()?.drmProvider
            }
        } else {
            return {
                serverKey: getShakaServerKey(props.voltApi),
                widevineLicenseUrl: drm.licenseUrl,
            };
        }
    }

    const gfkS2sFonction = (w: any, d: any, c: any, s: any, id: any) => {
        if (d.getElementById(id)) {
            return;
        }
    
        w.gfkS2sConf = c;
        w.gfkS2sConf = {
            media: c.mediaId,
            url: c.url,
            device: c.device,
            type: c.type,
            logLevel: c.logLevel,
        };
        w[id] = {};
        w[id].agents = [];
        var api = [
            "playStreamLive",
            "playStreamOnDemand",
            "stop",
            "skip",
            "screen",
            "volume",
            "impression",
        ];
        w.gfks = (function () {
            function f(sA: any, e: any, cb: any) {
                return function () {
                    sA.p = cb();
                    sA.queue.push({
                        f: e,
                        a: arguments,
                    });
                };
            }
    
            function s(c: any, pId: any, cb: any) {
                var sA = {
                    queue: [],
                    config: c,
                    cb: cb,
                    pId: pId,
                };
                for (var i = 0; i < api.length; i++) {
                    var e = api[i];
                    sA[e] = f(sA, e, cb);
                }
                return sA;
            }
    
            return s;
        })();
        w[id].getAgent = function (cb: any, pId: any) {
            var a = {
                a: new w.gfks(
                    c,
                    pId || "",
                    cb ||
                        function () {
                            return 0;
                        }
                ),
            };
    
            function g(a: any, e: any) {
                return function () {
                    return a.a[e].apply(a.a, arguments);
                };
            }
    
            for (var i = 0; i < api.length; i++) {
                var e = api[i];
                a[e] = g(a, e);
            }
            w[id].agents.push(a);
            return a;
        };
    
        var lJS = function (eId: any, url: any) {
            var tag = d.createElement(s);
            var el = d.getElementsByTagName(s)[0];
            tag.id = eId;
            tag.async = true;
            tag.type = "text/javascript";
            tag.src = url;
            tag.setAttribute("crossorigin", "anonymous");
            el.parentNode.insertBefore(tag, el);
        };
        lJS(id, c.url);
    };

    const getProgramInfo = () : ConvivaMetadata => {
        return {
            'Conviva.assetName': program()?.title || program()?.name || 'N/A',
            'Conviva.duration': program()?.isDvr?.() ? program()?.duration : (program()?.duration / 1000),
            ['c3.cm.categoryType']: program()?.isLive?.() ? program()?.categories?.join(',') : program()?.genres ? program()?.genres?.join(',') : program()?.genre,
            ['c3.cm.id']: program()?.id || 'N/A',
            ['c3.cm.seriesName']: program()?.title || 'N/A',
            ['c3.cm.seasonNumber']: program()?.seasonNumber ? program()?.seasonNumber : 'N/A',
            ['c3.cm.showTitle']: program()?.title || 'N/A',
            ['c3.cm.episodeNumber']:
                program()?.isEpisode?.() ? program()?.number?.toString?.() : program()?.episodeNumber?.toString?.() || 'N/A',
            ['c3.cm.genreList']: program()?.isLive?.() ? program()?.categories?.join(',') : program()?.genres ? program()?.genres?.join(',') : program()?.genre,
            ['c3.cm.genre']: program()?.isLive?.() ? program()?.categories?.join(',') : program()?.genres ? program()?.genres?.join(',') : program()?.genre,
            ['c3.cm.contentType']:
                program()?.isDvr?.() ? 'DVR'
                    : isStartOver() ? 'StartOver'
                        : program()?.isChannel?.() || program()?.isLiveNow?.() ? 'Live'
                            : isCatchUp(program()?.startTime, channel()?.rollingBufferDuration) ? 'Catchup'
                                : program()?.isVod?.() ? 'VOD' : 'N/A',
            ['c3.cm.railName']: props.state?.railName ?? 'N/A',
            'Conviva.streamType': program()?.isDvr?.() ? 'VOD'
                : program()?.isChannel?.() || program()?.isLiveNow?.() ? 'LIVE'
                    : isCatchUp(program()?.startTime, channel()?.rollingBufferDuration) ? 'VOD'
                        : program()?.isVod?.() ? 'VOD' : 'UNKNOWN',
        }
    }

    const reportPlaybackInitialData = (url: string) => {
        const meta = {
            'Conviva.applicationName': convivaConfig()?.applicationName || 'webStv',
            'Conviva.defaultResource': "broadpeak",
            'Conviva.channelNumber': channel()?.channelNumber ? channel()?.channelNumber?.toString?.() : 'N/A',
            ['c3.cm.channel']: channel()?.title ? channel()?.title : 'N/A',
            ['c3.cm.brand']: 'N/A',
            ['c3.cm.affiliate']: 'N/A',
            ['c3.cm.name']: 'N/A',
            ['c3.app.version']: getVersion(),
            'Conviva.frameworkName': 'Nagra Player',
            'Conviva.frameworkVersion': '5.24.0',
            'Conviva.streamUrl': url,
            'Conviva.viewerId': profile()?.userId || profile()?.customerId,
            // 'Conviva.deviceCategory': isWeb ? ConvivaConstants.DeviceCategory.WEB : ConvivaConstants.DeviceCategory.SMART_TV
        }

        const finalMetadata = Object.assign(meta, getProgramInfo())
        reportPlaybackRequested(finalMetadata, true)
    }

    const getVoltError = (errorCode: number, suffix?: string, rawError?: any) => {
        const { VoltError } = props.voltApi
        const _errorCode = playerCodeToVoltCode(errorCode, suffix);
        const voltError = VoltError.videoPlayerErrorToVoltError({
            code:_errorCode, 
            rawError: rawError,
            component: mediaEngine === 'nagra' ? 'Nagra' : undefined,
            exception: rawError
        })

        return voltError
    }

    function errorHandler(severity: number, errorCode: number, message: string, ssmType?: string) {

        if(isTileDetail()) return;

        let voltError
        let ssmSuffix = ''
        if (ssmType) {
            ssmSuffix = ssmErrorCodes[errorCode] || ''
            voltError = getVoltError(ssmError[ssmType], ssmSuffix, ssmError[ssmType] + ssmSuffix)
        } else {
            voltError = getVoltError(errorCode)
        }

        const context = (voltError.context || voltError.defaultContext || '')
        const code = (voltError?.customNumCode ?? voltError.numCode ?? '')
        const errorSuffix  =  (context ? context.split('-')?.[0] + '-' : '') + code 

        const descriptionKey = { id: voltError?.code }
        const translatedKey = getI18n()?.formatMessage(descriptionKey)
        const bodyDescription = translatedKey && translatedKey !== descriptionKey.id ? translatedKey : voltError.description
        if (severity === 2 && !criticalError() && errorCode !== 1002) {
            setCriticalError({
                header: getI18n()?.formatMessage({ id: 'ERROR' }) + ' ' + errorSuffix, 
                message: bodyDescription ?? message
            })
            setOverlay(PlayerOverlays.PLAYER_ERROR_POPUP)
            setLoading(false)
            dispatch(setLastPlayerError(voltError))
        }

        //Error not handled if playback is working.
        if (loading() && severity !== 2 && !criticalError()) {
            if (errorCode === 1002 && !loadedMetaData()) {
                if (isWeb) {
                    mediaEngine === 'nagra' && reportPlaybackFailed(voltError);
                    if (props.state?.tileClickNavigate) {
                        props.navigate(-1)
                    } else {
                        props.navigate("/")
                    }
                } else {
                    props.publish('player', undefined)
                }
            } else {
                mediaEngine === 'nagra' && reportPlaybackError(voltError, 1)
            }

            show({
                title: getI18n()?.formatMessage({ id: 'ERROR' }) + ' ' + errorSuffix, 
                description: voltError?.description ?? message,
                type: ToastType.ERROR,
                sticky: false,
            })
            dispatch(setLastPlayerError(voltError))
        }

    }

    const loadSource = (params: any) => {
        player().loadSrc(params).then((res: any) => {
            const error = res?.[0]
            if (
                mediaEngine !== 'nagra' &&
                error?.severity === 2 &&
                error?.code !== 7000 &&
                error?.code !== 7001
            ) {
                errorHandler(error?.severity, error?.code, error?.message)
            }

            if (mediaEngine === 'shaka') {
                // listening to licence failure as shaka player doens't throw any error on that
                player().player?.getNetworkingEngine?.()?.addEventListener?.('retry', (event: any) => {
                    // severity doesn't reflect immediately so added timeout
                    setTimeout(() => {
                        if (event.error && event.error.severity === 2 && event.error.data?.[0] === drmConfig()?.widevineLicenseUrl) {
                            errorHandler(2, 370, '')
                        }
                    }, 3000);
                });
            }
        })
        reportPlaybackInitialData(params.url)
        setStreamId(params.url)
        if (props?.isReloaded) {
            dispatch(setPlayerPaused(true))
            props?.setIsReloaded?.(false)
        }
    }

    const onPlaybackUrlSuccess = async (data: any) => {
        if (player()) {
            setOverlay(undefined)
            matchEventsTimer && clearInterval(matchEventsTimer)
            matchStatsTimer && clearInterval(matchStatsTimer)

            let streamUrl = data.url
            let params
            if (mediaEngine === 'nagra') {
                const url: any = await getSessionUrl(getConfig().broadpeak, streamUrl)
                if (url) {
                    streamUrl = url
                }
                params = {
                    url: streamUrl,
                    headers: data.drm?.headers,
                    voltApi: props?.voltApi,
                }
            } else {
                params = {
                    url: streamUrl,
                    drm: drmConfig(),
                }
            }
            loadSource(params)
        } else {
            setTimeout(() => {
                onPlaybackUrlSuccess(data)
            }, 50)
        }
    }

    const handleTokenFailure = () => {
        if (getConfig()?.client === 'starhub') {
            dispatch(resetLastPlayerError())
            setShowUnsuscribedPopUp(true)
            setDrmConfig({ errored: true })
        } else {
            if (getConfig()?.agama?.enable) {
                const agamaInstance = AgamaAnalytics.getInstance(getConfig()?.agama, undefined, undefined, undefined, undefined, undefined, isWeb, undefined)
                if (agamaInstance && agamaInstance.getEmpClient) {
                    agamaInstance.sessionMetadata(SessionMetadata.CONTENT_TITLE, channel()?.channelNumber + " " + channel()?.name);
                    agamaInstance.sessionMetadata(SessionMetadata.CONTENT_SERVICE_TITLE, channel()?.number ? channel()?.number + " " + channel()?.name : channel() ? channel()?.channelNumber +" "+ channel()?.title : "unknown");
                    agamaInstance.sessionMetadata(SessionMetadata.CONTENT_DESCRIPTION, program()?.description);
                    agamaInstance.getEmpClient().viewStateExtended(8, '309', "User is not subscribed to this channel")
                }
            }
            // Exit the player when playback errored due to missing drm id or entitlement revoked
            if (isWeb) {
                mediaEngine === 'nagra' && reportPlaybackFailed(playerError());
                if (props.state?.tileClickNavigate) {
                    props.navigate(-1)
                } else {
                    props.navigate("/")
                }
            } else {
                props.publish('player', undefined)
            }
        }
    }

    const requestPlaybackUrl = (params: IRequestPlaybackParama) => {
        const { isMulticam = false, isStartOver = false, isTimeShiftedLive = false, offset = 0, backToLive = false } = params
        setDrmConfig(undefined) // setting drmconfig to undefined when requested playback url

        // When in startOver section we want user to have metadata with same asset so not changing program in playback
        // But when user clicks on backToLive we want program to be updated with the current live program
        let liveProgramId
        // Both starhub and ooredoo send endtime different (ooredoo in milliseconds and starhub in seconds)
        const endTime = program()?.endTime?.toString()?.length === 13 ? program()?.endTime : program()?.endTime * 1000 // converting to milliseconds
        if ((backToLive || isTimeShiftedLive) && endTime < Date.now()) {
            const filteredProgram: any = Object.values(programsState()).find((p: any) => p?.channelId === channelId() && p?.isLiveNow?.())
            if (filteredProgram?.id !== programId()) {
                setProgram(filteredProgram)
                liveProgramId = filteredProgram?.id
            }
        }

        if (!!props.state?.startFromBeginning && program()) {
            // When (Watch From Begining) we will update program with bookmrak 0 so that extra seekTo method will be saved
            const p = program()
            p.bookmark = 0
            setProgram(p)
        }

        setLoading(true)
        setLoadedMetaData(false)
        const activeCamAngle = channel()?.multiCamRef.find((i: any) => i.platform_id === multicamData()?.assetId)
        if (isMulticam || (isStartOver && multicamData() && activeCamAngle)) {
            // Using programId and channelId from player state as we will be changing angle in same asset
            dispatch(getMulticamUrl({
                programId: liveProgramId ?? programId(),
                channelId: channelId(),
                cameraAngleDetails: params?.angle ?? activeCamAngle,
                isStartOver: isStartOver,
                success: (data: any) => {
                    const drmConfig = getDrmConfig(data?.drm)
                    setDrmConfig(drmConfig)
                    onPlaybackUrlSuccess(data)
                },
                failed: handleTokenFailure,
                offset: offset
            }))
        } else {
            dispatch(getPlaybackUrl({
                programId: liveProgramId ?? program()?.id,
                channelId: channel()?.id,
                shouldCheckPcRestriction: false,
                isTimeshiftedLive: isTimeShiftedLive,
                isTrailer: !!props.state?.isTrailer,
                isStartOver: isStartOver,
                success: (data: any) => {
                    const drmConfig = getDrmConfig(data?.drm)
                    setDrmConfig(drmConfig)
                    onPlaybackUrlSuccess(data)
                    if (getConfig()?.client === 'ooredoo' && data?.url) {
                        // stream url required for agama session to be started
                        // as ooredoo do not have broadpeak setting url from here doesn't affect the final stream url
                        setStreamId(data?.url)
                    }
                },
                failed: handleTokenFailure,
                offset: offset,
                resetBookmarkBeforePlayback: isStartOver ? false: !!props.state?.startFromBeginning
            }))
        }
    }

    createEffect(
        on(
            () => playerError(),
            () => {
                if (playerError()?.code === 'PARENTAL_LOCKED') {
                    show({
                        title: getI18n()?.formatMessage({id: 'ERROR'}),
                        description: playerError()?.errorCode + ' ' + playerError()?.description,
                        type: ToastType.ERROR,
                        sticky: false,
                    })
                }
                if (playerError()?.code === 'PLAYER_CONTENT_ID_ERROR' || playerError()?.code === 'PLAYER_ENTITLEMENT_REVOKED') {
                    handleTokenFailure()
                }
            }
        )
    )

    createEffect(
        on(() => matches()?.[gameId()]?.status, () => {
            const match = matches()?.[gameId()]
            const status = match?.status
            setMatchStatus(status)

            if (match && status !== MatchStatus.playing && matchEventsTimer) {
                clearInterval(matchEventsTimer)
            }

            if (match && ![MatchStatus.playing, MatchStatus.fixture, MatchStatus.suspended].includes(status) && matchStatsTimer) {
                clearInterval(matchStatsTimer)
            }

            if (match && status === MatchStatus.played && getConfig()?.eplFeatures?.matchEvents) {
                dispatch(fetchMatchEvents({ id: gameId() }))
            }

            if (match && status === MatchStatus.playing && !matchEventsTimer && getConfig()?.eplFeatures?.matchEvents) {
                dispatch(fetchMatchEvents({ id: gameId() }))
                matchEventsTimer = setInterval(
                    () => {
                        dispatch(fetchMatchEvents({ id: gameId() }))
                    },
                    getConfig()?.opta?.statsPeriodAutoRefresh || 60000
                )
            }
        })
    )

    createEffect(
        on(() => gameId(), () => {
            if (gameId() && getConfig()?.eplFeatures?.matchStatistics && getConfig()?.opta?.enabled) {
                dispatch(fetchMatchStats({ id: gameId() }))

                if (program()?.isLiveNow?.()) {
                    matchStatsTimer = setInterval(
                        () => {
                            dispatch(fetchMatchStats({ id: gameId() }))
                        },
                        getConfig()?.opta?.statsPeriodAutoRefresh || 60000
                    )
                }
            }
        })
    )

    createEffect(
        on(() => programId(), () => {
            // Setting up program on live channel when the next program is started during the live playback
            if (programId() && programId() !== program()?.id && channelId() === channel()?.id) {
                let playingProgram = programsState()[programId()]
                setProgram(playingProgram)
                setGameId(playingProgram?.optaDetails?.optaFixtureId)
                setIsLiveProgram(!!playingProgram?.isLiveNow?.())
                reportPlaybackRequested(getProgramInfo())
            }
        })
    )

    createEffect(
        on([() => props.userPcRestrictions, () => props.state?.isTrailer], () => {
            setUserPcRestrictions(props?.userPcRestrictions)
            setIsTrailer(!!props?.state?.isTrailer)
        })
    )

    createEffect(
        on([() => props.state?.isTileDetail], () => {
            props.state?.isTileDetail && cleanupData()
            setIsTileDetail(!!props?.state?.isTileDetail)
        })
    )

    createEffect(
        on([() => props?.purchasePopupOpen], () => {
            setPurchasePopUpOpen(!!props?.purchasePopupOpen)
        })
    )

    const cleanupData = () => {
        dispatch(setSelectedAudioTrackForCurrentPlayback(''))
        dispatch(setSelectedTextTrackForCurrentPlayback(''))
        dispatch(setAvailableAudioTracks([]))
        dispatch(setAvailableTextTracks([]))
        dispatch(stopPlayer())
        setProgram(undefined)
        setChannel(undefined)
        // setPlayer(undefined)
        setDrmConfig(undefined)
        setMatchStatus(undefined)
        setLoadedMetaData(false)
        setOverlay(undefined)
        setShowParental(false)
        setKeyPressed(undefined)
        setPausedByUser(false)
        setCriticalError(undefined)
        setIsFullscreen(false)
        setAdData(undefined)
        setShowUnsuscribedPopUp(false)
    }

    createEffect(
        on([() => props.program, () => props.channel], () => {
            if ((program()?.id === props.program?.id || channel()?.id === props.program?.id) && isTileDetail()) {
                return
            }
            if ((program()?.id !== props.program?.id || channel()?.id !== props.program?.id) && !isTileDetail()) {
                // This means playback is going on and user played another asset from player
                cleanupData()
            }
            setTimeout(() => {
                setProgram(props.program)
                setChannel(props.channel)
                setGameId(props.program?.optaDetails?.optaFixtureId)
                setIsLiveProgram(!!props.program?.isLiveNow?.())
                getConfig()?.client === 'ooredoo' ? 
                setIsStartOverAllowed(props?.channel?.getRollingBufferCutOff() <= props?.program?.startTime)
                :
                setIsStartOverAllowed((props.channel?.startOver && !props.channel?.isFiller?.()))
                requestPlaybackUrl({})
            }, 200)
        })
    )

    onMount(() => {
        if (!isTileDetail()) dispatch(analyticsLogScreen({ screenName: getConfig()?.analytics?.firebaseScreen.PLAYBACK }))
        if (!getConfig()?.conviva) return
        setConvivaConfig(getConfig().conviva)
    })

    onCleanup(cleanupData)

    const values = {
        program,
        channel,
        programId: () => programId(),
        channelId: () => channelId(),
        voltApi: () => props.voltApi,
        player,
        setPlayer,
        multicamData: () => multicamData(),
        loading,
        setLoading,
        gameId,
        matchStatus,
        canBookmark: () => !!(program()?.isVod?.() || program()?.isDvr?.() || program()?.isPastLive?.()) && !isTrailer(),
        userPcRestrictions,
        isTrailer,
        playerPublish: props?.publish,
        loadedMetaData,
        setLoadedMetaData,
        isLiveProgram,
        navigate: props.navigate,
        overlay,
        setOverlay,
        showParental,
        setShowParental,
        keyPressed,
        setKeyPressed,
        isStartOverAllowed,
        pausedByUser,
        setPausedByUser,
        getDrmConfig: drmConfig,
        requestPlaybackUrl,
        criticalError,
        setCriticalError,
        errorHandler,
        streamId,
        gfkS2sFonction: gfkS2sFonction,
        setGfk,
        gfk,
        isFullscreen,
        setIsFullscreen,
        adData,
        setAdData, 
        isTileDetail,
        setIsTileDetail,
        showUnsuscribedPopUp,
        purchasePopUpOpen,
    }

    return (
        <PlayerProviderContext.Provider value={values}>
            <Show when={(program() || channel()) && drmConfig()}>
                {props.children}
            </Show>

            <Show when={(!drmConfig() || !player()) && !isTileDetail()}>
                <div class="player-fallback-spinner"><Spinner /></div>
            </Show>
        </PlayerProviderContext.Provider>
    );
}
